if (!customElements.get('yqn-instagram-gallery')) {
  class YQNInstagramGallery extends HTMLElement {
    connectedCallback() {
      this.galleryRoot = this.closest('.yqn-instagram') || this;
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.previousButton = this.galleryRoot.querySelector('[data-gallery-previous]');
      this.nextButton = this.galleryRoot.querySelector('[data-gallery-next]');
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));

      if (!this.viewport || this.items.length === 0) return;

      this.onScroll = this.requestControlsUpdate.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.viewport);
      }

      this.updateControls();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
    }

    get stepSize() {
      if (!this.items[0]) return this.viewport.clientWidth;
      const gap = Number.parseFloat(window.getComputedStyle(this.querySelector('.yqn-instagram__track')).columnGap) || 0;
      return this.items[0].getBoundingClientRect().width + gap;
    }

    get scrollPosition() {
      return Math.abs(this.viewport.scrollLeft);
    }

    get maxScroll() {
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    }

    move(direction) {
      const rtlMultiplier = window.getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.viewport.scrollBy({ left: direction * rtlMultiplier * this.stepSize, behavior: 'smooth' });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    }

    requestControlsUpdate() {
      if (this.controlsTicking) return;
      this.controlsTicking = true;
      requestAnimationFrame(() => {
        this.updateControls();
        this.controlsTicking = false;
      });
    }

    updateControls() {
      const position = this.scrollPosition;
      const maxScroll = this.maxScroll;
      const hasOverflow = maxScroll > 2;
      const edgeTolerance = Math.max(8, this.stepSize * 0.02);

      if (this.previousButton) this.previousButton.disabled = !hasOverflow || position <= edgeTolerance;
      if (this.nextButton) this.nextButton.disabled = !hasOverflow || position >= maxScroll - edgeTolerance;
      this.classList.toggle('is-static', !hasOverflow);
      this.galleryRoot.classList.toggle('is-static-gallery', !hasOverflow);
    }

    startAutoplay() {
      const autoplayEnabled = this.dataset.autoplay === 'true';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!autoplayEnabled || reduceMotion || document.hidden || this.autoplayTimer) return;

      const delay = Math.max(3, Number(this.dataset.autoplaySpeed || 5)) * 1000;
      this.autoplayTimer = window.setInterval(() => {
        if (this.scrollPosition >= this.maxScroll - 2) {
          this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.move(1);
        }
      }, delay);
    }

    pauseAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    handleVisibilityChange() {
      if (document.hidden) this.pauseAutoplay();
      else this.startAutoplay();
    }

    handleBlockSelect(event) {
      const block = event.target.closest?.('[data-gallery-item]');
      if (!block || !this.contains(block)) return;
      this.pauseAutoplay();
      block.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  customElements.define('yqn-instagram-gallery', YQNInstagramGallery);
}
